// Demo service for local storage of scans when backend is unavailable
import { nanoid } from 'nanoid';

export interface DemoScan {
  id: string;
  guardName: string;
  checkpointName: string;
  siteName: string;
  timestamp: string;
  status: 'on-time' | 'late' | 'missed' | 'early';
  notes?: string;
  lat?: number;
  lng?: number;
  accuracy?: number;
  qrToken: string;
}

const DEMO_STORAGE_KEY = 'emory_demo_scans';
const DEMO_GUARDS = ['John Doe', 'Jane Smith', 'Mike Johnson', 'Sarah Williams'];
const DEMO_CHECKPOINTS = [
  'Main Entrance',
  'Parking Garage Level 1',
  'Emergency Exit A',
  'Server Room',
  'Reception Desk',
  'Loading Dock',
  'Roof Access',
  'Security Office'
];

// Get all demo scans from localStorage
export function getDemoScans(): DemoScan[] {
  const stored = localStorage.getItem(DEMO_STORAGE_KEY);
  if (!stored) {
    return getInitialDemoScans();
  }
  
  try {
    const scans = JSON.parse(stored);
    return Array.isArray(scans) ? scans : getInitialDemoScans();
  } catch {
    return getInitialDemoScans();
  }
}

// Save demo scans to localStorage
export function saveDemoScans(scans: DemoScan[]) {
  localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(scans));
}

// Add a new demo scan
export function addDemoScan(qrToken: string, notes?: string, guardName?: string): DemoScan {
  const scans = getDemoScans();
  
  const newScan: DemoScan = {
    id: nanoid(),
    guardName: guardName || 'Demo Guard',
    checkpointName: getCheckpointNameFromToken(qrToken),
    siteName: 'Emory University Campus',
    timestamp: new Date().toISOString(),
    status: getRandomStatus(),
    notes: notes || '',
    lat: 33.7490 + (Math.random() - 0.5) * 0.01, // Atlanta area coordinates with small variation
    lng: -84.3880 + (Math.random() - 0.5) * 0.01,
    accuracy: Math.floor(Math.random() * 15) + 5, // 5-20 meter accuracy
    qrToken
  };
  
  // Add to beginning of array (most recent first)
  scans.unshift(newScan);
  
  // Keep only last 50 scans
  if (scans.length > 50) {
    scans.splice(50);
  }
  
  saveDemoScans(scans);

  return newScan;
}

// Get checkpoint name from QR token
function getCheckpointNameFromToken(token: string): string {
  // Map common demo tokens to friendly names
  const tokenMap: Record<string, string> = {
    'test-checkpoint-lobby-001': 'Main Entrance Lobby',
    'test-demo-scan-123': 'Parking Garage Level 1',
    'test-patrol-point-a': 'Emergency Exit A',
  };
  
  if (tokenMap[token]) {
    return tokenMap[token];
  }
  
  // Generate a checkpoint name for unknown tokens
  const randomCheckpoint = DEMO_CHECKPOINTS[Math.floor(Math.random() * DEMO_CHECKPOINTS.length)];
  return randomCheckpoint;
}

// Generate random status based on realistic distribution
function getRandomStatus(): DemoScan['status'] {
  const rand = Math.random();
  if (rand < 0.75) return 'on-time';     // 75% on-time
  if (rand < 0.9) return 'early';       // 15% early  
  if (rand < 0.98) return 'late';       // 8% late
  return 'missed';                       // 2% missed
}

// Generate initial demo data
function getInitialDemoScans(): DemoScan[] {
  const scans: DemoScan[] = [];
  const now = new Date();
  
  // Generate 10 initial scans over the past 4 hours
  for (let i = 0; i < 10; i++) {
    const minutesAgo = Math.floor(Math.random() * 240); // 0-240 minutes ago
    const timestamp = new Date(now.getTime() - minutesAgo * 60 * 1000);
    
    scans.push({
      id: nanoid(),
      guardName: DEMO_GUARDS[Math.floor(Math.random() * DEMO_GUARDS.length)],
      checkpointName: DEMO_CHECKPOINTS[Math.floor(Math.random() * DEMO_CHECKPOINTS.length)],
      siteName: 'Emory University Campus',
      timestamp: timestamp.toISOString(),
      status: getRandomStatus(),
      notes: Math.random() > 0.7 ? 'All clear, no issues' : '',
      lat: 33.7490 + (Math.random() - 0.5) * 0.01,
      lng: -84.3880 + (Math.random() - 0.5) * 0.01,
      accuracy: Math.floor(Math.random() * 15) + 5,
      qrToken: `demo-${nanoid(8)}`
    });
  }
  
  // Sort by timestamp (most recent first)
  scans.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  
  saveDemoScans(scans);
  return scans;
}

// Get demo statistics
export function getDemoStats() {
  const scans = getDemoScans();
  
  // Calculate stats from last 24 hours
  const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
  const recentScans = scans.filter(scan => new Date(scan.timestamp) > oneDayAgo);
  
  const onTimeScans = recentScans.filter(scan => scan.status === 'on-time').length;
  const lateScans = recentScans.filter(scan => scan.status === 'late').length;
  const missedScans = recentScans.filter(scan => scan.status === 'missed').length;
  const earlyScans = recentScans.filter(scan => scan.status === 'early').length;
  
  const totalScans = recentScans.length;
  const uniqueGuards = new Set(recentScans.map(scan => scan.guardName)).size;
  
  return {
    onDuty: uniqueGuards,
    completed: onTimeScans + earlyScans,
    missed: missedScans,
    compliance: totalScans > 0 ? Math.round(((onTimeScans + earlyScans) / totalScans) * 100 * 10) / 10 : 0,
    totalScans
  };
}

// Convert demo scans to API format for compatibility
export function getDemoActivity(limit: number = 5) {
  const scans = getDemoScans();
  
  return {
    items: scans.slice(0, limit).map(scan => ({
      id: scan.id,
      guard: { id: scan.guardName.toLowerCase().replace(' ', '_'), name: scan.guardName },
      checkpoint: { id: scan.checkpointName.toLowerCase().replace(' ', '_'), name: scan.checkpointName },
      site: { id: 'emory_campus', name: scan.siteName },
      assignmentId: `assignment_${scan.id}`,
      timestamp: scan.timestamp,
      lat: scan.lat,
      lng: scan.lng,
      scheduleStatus: scan.status,
      distanceMeters: scan.accuracy
    }))
  };
}

// Clear all demo data (for testing)
export function clearDemoData() {
  localStorage.removeItem(DEMO_STORAGE_KEY);
}

// Check if we should use demo mode (when API calls fail)
export function shouldUseDemoMode(): boolean {
  // Always use demo mode for now - can be configured later
  return true;
}